import { useRef, useState } from 'react';
import { CrawlerWidget } from '@/tampermonkey/widget';
import ls from '@/tampermonkey/local-store';
import { useAppSelector } from '@/store/hooks';

// Only this account sees the button, on top of the import.meta.env.DEV gate
// below -- per explicit request, not a security boundary (anyone with repo
// access could remove this check locally anyway).
const PREVIEW_ALLOWED_EMAIL = 'daeho@ggcontent.com';

/**
 * Dev-only trigger that mounts the REAL `CrawlerWidget` (src/tampermonkey/)
 * inside the admin app itself, fed sample data, so its current look/behavior
 * can be eyeballed without building + installing the userscript in a real
 * browser. Gated on BOTH `import.meta.env.DEV` (never rendered in a real
 * staging/production build) AND the logged-in account's email (see
 * `PREVIEW_ALLOWED_EMAIL`) -- so other local devs signed in as a different
 * admin don't see it either.
 *
 * Two things the widget normally gets from the real page are faked here:
 * - `window.location.href` (used to detect the platform/channel) -- the
 *   admin app's own URL obviously isn't one, so a fake YouTube channel URL is
 *   passed to the constructor's `hrefOverride` param instead.
 * - `GM_xmlhttpRequest` (Tampermonkey's cross-origin request API, not
 *   present outside the extension) -- stubbed on `window` to resolve with
 *   canned sample responses shaped like the real `admin/universal-crawls/*`
 *   endpoints, so the widget's own fetch/render logic runs unmodified.
 */

const SAMPLE_HREF = 'https://www.youtube.com/channel/UC_SAMPLE0000000000/about';

const SAMPLE_CAMPAIGNS = [
  '2ndwave', '8-bit', '86Talent', 'A2Z Influencers', 'Abstract', 'Ace Creators',
  'Action RPG Groups', 'Action RPGs', 'adrox.io', 'AFK Creators', 'Aftershock Media',
  'AIHIA', 'Aion 2 (NA)', 'AJ Marketing', 'AKA Virtual_ID_Vtuber', 'Allyance', 'AMIE',
  'Andrew Warren Group', 'Anime', 'ANYCOLOR (NIJISANJI)', 'Apollo MGMT', 'Arkadia',
].map((title, i) => ({ id: `sample-campaign-${i}`, title }));

const SAMPLE_QUEUE = [
  'Brian Tyler Cohen', 'Snow', 'Mr. Fruit Plays', 'BK Brent', 'Cygames EN',
  'Take & Mei Story たけめい ストーリー', 'AFK Arena', 'MrEzioPvP', 'DaveControl', 'Engineering Eternity',
].map((title, i) => ({ _id: `sample-queue-${i}`, channelId: `sample-channel-${i}`, title }));

const SAMPLE_HISTORY = [
  'CommonCookie', "Larxa's Vault", 'Strippin', 'FrostPrime VODS', 'Goofy Gang',
  'Wonky Quartet', 'RJ Siu', 'DUMBFOUNDEAD', 'MattVidPro', 'Akash Thakkar',
].map((title, i) => ({ _id: `sample-history-${i}`, title, channels: [{ channelId: `sample-channel-${i}` }] }));

/** Stubs `window.GM_xmlhttpRequest` to resolve sample data for every
 * `admin/universal-crawls/*` action the widget calls, instead of a real
 * network request (there is no Tampermonkey extension context here to make
 * one through). */
function installSampleGmXhr(): void {
  const stub: typeof GM_xmlhttpRequest = (opts) => {
    const action = opts.url.split('/').pop();
    const data = ((): unknown => {
      switch (action) {
        // No userId/crawlId -> renders the "addable" add-channel form.
        case 'load-channel': return { channelId: 'UC_SAMPLE0000000000', socials: {} };
        case 'load-queue': return SAMPLE_QUEUE;
        case 'load-history': return SAMPLE_HISTORY;
        case 'load-campaigns': return SAMPLE_CAMPAIGNS;
        default: return undefined;
      }
    })();
    window.setTimeout(() => {
      opts.onload?.({
        status: 200,
        statusText: 'OK',
        responseText: JSON.stringify({ success: true, data }),
        responseHeaders: '',
        finalUrl: opts.url,
      });
    }, 200);
  };
  (window as unknown as { GM_xmlhttpRequest: typeof GM_xmlhttpRequest }).GM_xmlhttpRequest = stub;
}

export function TampermonkeyPreviewButton() {
  const [shown, setShown] = useState(false);
  const disposeRef = useRef<(() => void) | null>(null);
  const email = useAppSelector((s) => s.envs.currentUser?.email);

  if (!import.meta.env.DEV || email !== PREVIEW_ALLOWED_EMAIL) return null;

  const hide = () => {
    disposeRef.current?.();
    disposeRef.current = null;
    setShown(false);
  };

  const show = () => {
    installSampleGmXhr();
    // Force the "open" panel + a fake API key so the widget renders its full
    // form/queue/history immediately instead of the collapsed ">>" button or
    // the "enter API key" prompt.
    ls.setItem('isWidgetOpen', true);
    ls.setItem('ApiKey', 'dev-preview-key');
    disposeRef.current = new CrawlerWidget(SAMPLE_HREF).mount();
    setShown(true);
  };

  return (
    <button
      type="button"
      className="tm-preview-btn"
      onClick={() => (shown ? hide() : show())}
      title="Mount the real Tampermonkey crawler widget with sample data, for local layout checks"
    >
      {shown ? 'Hide' : 'Show'} Tampermonkey sample
    </button>
  );
}
